"use client";

import { TIndustriesConfig } from "@/app/config";
import React from "react";
import { motion } from "motion/react";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
      duration: 0.6
    }
  }
};



const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
      delayChildren: 0.4
    }
  }
};


export default function IndustryInvestmentFlows({config}:{config: TIndustriesConfig['industryInvestmentFlows']}) {
  return (
    <motion.section 
      className="py-16 lg:py-20 bg-white relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={containerVariants}
    >
      {/* Animated background elements */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-green-50/30"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5 }}
      />

      {/* Flowing connection lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
        <motion.path
          d="M 100 200 Q 400 100 700 300"
          stroke="url(#gradient1)"
          strokeWidth="2"
          fill="none"
          variants={{
            hidden: { pathLength: 0, opacity: 0 },
            visible: {
              pathLength: 1,
              opacity: 0.3,
              transition: {
                duration: 2,
                ease: "easeInOut",
                delay: 1
              }
            }
          }}
        />
        <motion.path
          d="M 200 400 Q 500 300 800 500"
          stroke="url(#gradient2)"
          strokeWidth="2"
          fill="none"
          variants={{
            hidden: { pathLength: 0, opacity: 0 },
            visible: {
              pathLength: 1,
              opacity: 0.3,
              transition: {
                duration: 2,
                ease: "easeInOut",
                delay: 1
              }
            }
          }}
        />
        <defs>
          <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0" />
            <stop offset="50%" stopColor="#3B82F6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0" />
            <stop offset="50%" stopColor="#10B981" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          variants={ {
            hidden: { opacity: 0, y: 50 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: [0.25, 0.46, 0.45, 0.94]
              }
            }
          }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              duration: 0.8, 
              ease: "easeOut",
              delay: 0.1 
            }}
          >
            {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              duration: 0.8, 
              ease: "easeOut",
              delay: 0.2 
            }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Investment Cards Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          variants={gridVariants}
        >
          {config.investmentData.map((investment, index) => (
            <motion.div
              key={index}
              className={`${investment.bgColor} ${investment.borderColor} border rounded-2xl p-8 hover:shadow-xl transition-shadow duration-500 relative overflow-hidden cursor-pointer`}
              variants={ {
                hidden: { 
                  opacity: 0, 
                  y: 80,
                  scale: 0.8,
                  rotateX: -20
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  rotateX: 0,
                  transition: {
                    duration: 0.8,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    staggerChildren: 0.15,
                    delayChildren: 0.2
                  }
                }
              }}
              whileHover={{
                scale: 1.05,
                y: -10,
                rotateX: 5,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                transition: {
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
              style={{ perspective: 1000 }}
            >
              {/* Background pattern */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"
                variants={ {
                  hidden: { opacity: 0, scale: 0.8 },
                  visible: {
                    opacity: 0.05,
                    scale: 1,
                    transition: {
                      duration: 1.2,
                      ease: "easeOut"
                    }
                  },
                  hover: {
                    opacity: 0.1,
                    scale: 1.1,
                    transition: {
                      duration: 0.4
                    }
                  }
                }}
                initial="hidden"
                animate="visible"
                whileHover="hover"
              />

              {/* Animated geometric pattern */}
              <motion.div
                className="absolute top-0 right-0 w-32 h-32 opacity-5"
                style={{
                  backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
                  backgroundSize: '10px 10px'
                }}
                animate={{
                  rotate: 360
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />

              {/* Pulsing accent dot */}
              <motion.div
                className="absolute bottom-4 left-4 w-2 h-2 bg-blue-500 rounded-full"
                animate={ {
                  scale: [1, 1.05, 1],
                  transition: {
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut"
                  }
                }}
                style={{ zIndex: 10 }}
              />

              {/* Growth Badge */}
              <motion.div 
                className="absolute top-6 right-6 z-20"
                variants={ {
                  hidden: { 
                    opacity: 0, 
                    scale: 0,
                    rotate: -90,
                    x: 50
                  },
                  visible: {
                    opacity: 1,
                    scale: 1,
                    rotate: 0,
                    x: 0,
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 25,
                      delay: 0.3
                    }
                  },
                  hover: {
                    scale: 1.15,
                    rotate: [0, -5, 5, 0],
                    transition: {
                      duration: 0.4,
                      ease: "easeInOut"
                    }
                  }
                }}
                whileHover="hover"
              >
                <motion.span
                  className={`${investment.badgeColor} text-white text-sm font-semibold px-3 py-1 rounded-full shadow-lg`}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                    delay: index * 0.3 + 0.4
                  }}
                  whileHover={{
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)"
                  }}
                >
                  {investment.growth}
                </motion.span>
              </motion.div>

              {/* Content */}
              <div className="pr-16 relative z-10">
                {/* Title and Description */}
                <motion.div 
                  className="mb-8"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.1
                      }
                    }
                  }}
                >
                  <motion.h3 
                    className={`text-2xl font-semibold ${investment.titleColor} mb-3`}
                    variants={ {
                      hidden: { opacity: 0, x: -40 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.7,
                          ease: "easeOut"
                        }
                      }
                    }}
                    whileHover={{
                      x: 5,
                      transition: { duration: 0.2 }
                    }}
                  >
                    {investment.title}
                  </motion.h3>
                  <motion.p 
                    className="text-gray-600 text-base leading-relaxed"
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                          delay: 0.1
                        }
                      }
                    }}
                  >
                    {investment.description}
                  </motion.p>
                </motion.div>

                {/* Investment Amount */}
                <motion.div 
                  className="text-center"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.1
                      }
                    }
                  }}
                >
                  <motion.div 
                    className={`text-4xl font-bold ${investment.amountColor} mb-2 relative`}
                    variants={{
                      hidden: { 
                        opacity: 0, 
                        scale: 0.5,
                        y: 30
                      },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        y: 0,
                        transition: {
                          type: "spring",
                          stiffness: 200,
                          damping: 20,
                          delay: 0.2
                        }
                      },
                      hover: {
                        scale: 1.1,
                        transition: {
                          duration: 0.3,
                          ease: "easeOut"
                        }
                      }
                    }}
                    whileHover="hover"
                  >
                    {/* Animated underline */}
                    <motion.div
                      className="absolute bottom-0 left-1/2 h-1 bg-current rounded-full"
                      initial={{ width: 0, x: "-50%" }}
                      whileInView={{ width: "60%" }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.8,
                        delay: index * 0.3 + 0.8,
                        ease: "easeOut"
                      }}
                    />
                    <div className="relative z-10 inline-block leading-none pb-4">{investment.amount}</div> 
                  </motion.div>
                  <motion.p 
                    className="text-sm text-gray-600"
                    variants={{
                      hidden: { opacity: 0, y: 15 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                          delay: 0.3
                        }
                      }
                    }}
                  >
                    {investment.year}
                  </motion.p>
                </motion.div>
              </div>

              {/* Hover glow effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-green-500/5 rounded-2xl"
                initial={{ opacity: 0 }}
                whileHover={{ 
                  opacity: 1,
                  transition: { duration: 0.3 }
                }}
              />

              {/* Corner accent */}
              <motion.div
                className="absolute bottom-0 right-0 w-20 h-20"
                style={{
                  background: `linear-gradient(135deg, transparent 50%, ${investment.titleColor}15 50%)`
                }}
                initial={{ scale: 0, rotate: 45 }}
                whileInView={{ scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.3 + 0.6,
                  ease: "backOut"
                }}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Floating investment flow indicators */}
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.1 }}
          viewport={{ once: true }}
          transition={{ duration: 2, delay: 1 }}
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-blue-500 rounded-full"
              animate={{
                x: [0, 100, -100, 0],
                y: [0, -50, 50, 0],
                opacity: [0.1, 0.5, 0.1]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                delay: i * 2,
                ease: "easeInOut"
              }}
              style={{
                left: `${i * 100}px`,
                top: `${i * 50}px`
              }}
            />
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}